import { httpClient, httpGet, HttpMethod } from './http-client';

// Response type for file upload operations
export interface UploadFileItem {
  fileName: string;
  dbSuccess: boolean;
  key?: string;
  id?: number;
  isDeviceImage?: boolean;
}

export interface UploadFilesResponse {
  [key: string]: UploadFileItem;
}

// Response type for presigned URL
export interface PresignedUrlResponse {
  url: string;
  fileName: string;
}

// Response type for multipart upload initialization
export interface MultipartInitResponse {
  uploadId: string;
  key: string;
}

// Response type for multipart URL
export interface MultipartUrlResponse {
  url: string;
}

export const createUploadedFiles = (fileList: FormData): Promise<UploadFilesResponse> => {
  const headers = { 'Content-Type': 'multipart/form-data' };
  return httpClient<UploadFilesResponse>('/uploadedFiles', HttpMethod.Post, fileList, headers);
};

// Export info type
export interface ExportInfo {
  fileType?: string;
  rowCount?: number;
  structureTypeCategory?: string;
  filter: {
    items?: Array<{
      field?: string;
      operator?: string;
      value?: unknown;
    }>;
  };
  startDate?: string;
  endDate?: string;
  locationId?: number;
  includeChilds?: boolean;
  excludedColumns?: string;
  fileName?: string;
  selectedColumns?: Array<{ label: string; value: string }>;
  visibleColumnsField?: string[];
  sorting?: Array<Record<string, unknown>>;
  pageSize?: number;
  multiSearchQuery?: string;
  columnSelectionData?: unknown;
}

// Export response type
export interface ExportResponse {
  /** Pre-signed download URL. Absent when the export was too large to return
   *  inline and the BFF emailed it instead — see `sendEmail`. */
  url?: string;
  fileName?: string;
  /** Set by the BFF when a large export is delivered by email rather than as a
   *  download. `message` is the user-facing text to show instead of starting a
   *  download. */
  sendEmail?: boolean;
  message?: string;
}

/** Exports are generated server-side over the whole result set, so they take
 *  far longer than a page fetch. Without this, `http-client`'s 15s default
 *  aborts large exports and the user sees "Export failed". */
const EXPORT_TIMEOUT_MS = 60000;

/** Escapes only the characters that break query-string framing, leaving `,`
 *  and `=` intact.
 *
 *  Filter values ride the query string as `&<field>=<operator>,<value>`, so the
 *  comma is a delimiter the BFF splits on and `isAnyOf` values arrive
 *  comma-joined. A blanket `encodeURIComponent` would percent-encode those
 *  delimiters and change how every existing filter parses, so this escapes the
 *  narrower set that genuinely corrupts the request: `%` (first, so the escapes
 *  below are not double-decoded), `&` (starts a new parameter), `#` (truncates
 *  the URL at a fragment) and `+` (decodes to a space).
 *
 *  Known residual gap: a single filter value that itself contains a comma is
 *  still ambiguous on the wire. Resolving that needs the BFF's parser — logged
 *  for a follow-up rather than guessed at here. */
const escapeQueryValue = (value: unknown): string =>
  String(value)
    .replace(/%/g, '%25')
    .replace(/&/g, '%26')
    .replace(/#/g, '%23')
    .replace(/\+/g, '%2B');

export const exportFiles = (exportInfo: ExportInfo): Promise<ExportResponse> => {
  let url = `/export?fileType=${encodeURIComponent(exportInfo?.fileType ?? '')}`;

  if (exportInfo?.rowCount) {
    url += `&rowCount=${exportInfo?.rowCount}`;
  }

  if (exportInfo?.structureTypeCategory) {
    url += `&structureTypeCategory=${encodeURIComponent(exportInfo.structureTypeCategory)}`;
  }

  exportInfo?.filter.items?.forEach((filter) => {
    const field = encodeURIComponent(filter?.field ?? '');
    const operator = encodeURIComponent(filter?.operator ?? '');
    url += `&${field}=${operator},${escapeQueryValue(filter.value)}`;
  });

  if (exportInfo?.startDate) {
    url += `&startDate=${encodeURIComponent(exportInfo.startDate)}`;
  }

  if (exportInfo?.endDate) {
    url += `&endDate=${encodeURIComponent(exportInfo.endDate)}`;
  }

  if (exportInfo?.locationId !== undefined) {
    url += `&locationId=${exportInfo?.locationId}`;
  }

  if (exportInfo?.includeChilds !== undefined) {
    url += `&includeChilds=${exportInfo?.includeChilds}`;
  }

  if (exportInfo?.excludedColumns) {
    // Comma-joined field list — same delimiter caveat as filter values.
    url += `&excludedColumns=${escapeQueryValue(exportInfo.excludedColumns)}`;
  }

  return httpClient<ExportResponse>(url, HttpMethod.Post, exportInfo, undefined, EXPORT_TIMEOUT_MS);
};

export const deleteUploadedFile = (id: number | string): Promise<void> => {
  return httpClient<void>(`/uploadedFiles/delete/${id}`, HttpMethod.Put, null);
};

export const uploadFiles = (
  entityId: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing; callers pass raw FormData or File[]; typing this precisely is a separate cleanup.
  fileList: any,
  entityType: string,
  featureType?: string
): Promise<UploadFilesResponse> => {
  const headers = { 'Content-Type': 'multipart/form-data' };
  let url = `/uploadedFiles/${entityId}/entityType/${entityType}`;
  if (featureType) {
    url += `?featureType=${featureType}`;
  }
  return httpClient<UploadFilesResponse>(url, HttpMethod.Post, fileList, headers, 30000);
};

export const getUploadedFiles = (
  entityId: number,
  entityType: string,
  notIncludeUrlsByDefault?: boolean,
  signal?: AbortSignal
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing return type; typing as UploadedFile[] would be a separate cleanup.
): any => {
  let url = `/uploadedFiles/${entityId}/entityType/${entityType}`;
  if (notIncludeUrlsByDefault !== undefined) {
    url += `?notIncludeUrlsByDefault=${notIncludeUrlsByDefault}`;
  }
  return httpGet(url, signal);
};

export const permanantDeleteUploadedFile = (id: number): Promise<void> => {
  return httpClient<void>(`/uploadedFiles/${id}`, HttpMethod.Delete, null);
};

export const updateUploadedFileData = (id: number, isMain: boolean): Promise<void> => {
  return httpClient<void>(`/uploadedFile/data/${id}`, HttpMethod.Put, { isMain });
};

// Response for CSV file info
export interface UploadedCsvFileResponse {
  url: string;
  fileName: string;
}

export const getUploadedCsvFile = (
  key: string,
  signal?: AbortSignal
): Promise<UploadedCsvFileResponse> => {
  return httpGet<UploadedCsvFileResponse>(`/uploadedFiles/key/${encodeURIComponent(key)}`, signal);
};

export const getUploadedFilePresignedUrlById = (
  id: number,
  contentDispositionType?: string,
  signal?: AbortSignal
): Promise<PresignedUrlResponse> => {
  return httpGet<PresignedUrlResponse>(
    `/uploadedFiles/presignedUrl/${id}?contentDispositionType=${contentDispositionType}`,
    signal
  );
};

export const getUploadedFilePresignedUrlByKey = (
  key: string,
  mimeType?: string,
  contentDispositionType?: string,
  signal?: AbortSignal
): Promise<PresignedUrlResponse> => {
  return httpGet<PresignedUrlResponse>(
    `/uploadedFiles/presignedUrlKey/${encodeURIComponent(key)}?mimeType=${mimeType}&contentDispositionType=${contentDispositionType}`,
    signal
  );
};

export interface ManifestExportResponse {
  url: string;
  fileName?: string;
}

export const exportDispatchManifest = (shipmentId: number): Promise<ManifestExportResponse> => {
  return httpClient<ManifestExportResponse>(`/manifestExport/${shipmentId}`, HttpMethod.Post, {});
};

// Response type for user profile image upload
export interface ProfileImageUploadResponse {
  url: string;
  fileName?: string;
  key?: string;
}

export const uploadUserProfileImage = (
  entityId: number,
  fileList: FormData
): Promise<ProfileImageUploadResponse[]> => {
  const headers = { 'Content-Type': 'multipart/form-data' };
  return httpClient(
    `/uploadedFiles/user/${entityId}/profile?entityType=userProfiles`,
    HttpMethod.Post,
    fileList,
    headers
  );
};

// Multipart upload types
export interface MultipartPartData {
  ETag: string | null;
  PartNumber: number;
}

export interface MultipartCompleteResponse {
  id?: number;
  key: string;
  fileName: string;
}

export const initializeUploadedFilesMultipart = (
  fileName: string,
  entityId: number,
  entityType: string
): Promise<MultipartInitResponse> => {
  return httpClient<MultipartInitResponse>('/initializeUploadedFilesMultipart', HttpMethod.Post, {
    fileName,
    entityId,
    entityType,
  });
};

export const getUploadedFilesMultipartUrl = (
  fileName: string,
  partNumber: number,
  uploadId: string
): Promise<MultipartUrlResponse> => {
  return httpClient<MultipartUrlResponse>('/getUploadedFilesMultipartUrl', HttpMethod.Post, {
    fileName,
    partNumber,
    uploadId,
  });
};

export const completeUploadedFilesMultipart = (
  key: string,
  uploadId: string,
  parts: MultipartPartData[],
  originalFileName: string,
  entityId: number,
  entityType: string,
  mimeType: string
): Promise<MultipartCompleteResponse> => {
  return httpClient<MultipartCompleteResponse>('/completeUploadedFilesMultipart', HttpMethod.Post, {
    key,
    uploadId,
    parts,
    originalFileName,
    entityId,
    entityType,
    mimeType,
  });
};

export const abortUploadedFilesMultipartUpload = (key: string, uploadId: string): Promise<void> => {
  return httpClient<void>('/abortUploadedFilesMultipartUpload', HttpMethod.Post, { key, uploadId });
};
